import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Music2 } from "lucide-react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStaged: (postId: string) => void;
};

const AddFromTikTokDialog = ({ open, onOpenChange, onStaged }: Props) => {
  const [videoUrl, setVideoUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    const url = videoUrl.trim();
    if (!/^https:\/\/www\.tiktok\.com\/@[\w.-]+\/video\/\d+/.test(url)) {
      toast.error("Paste a full TikTok video URL (https://www.tiktok.com/@handle/video/...)");
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("tiktok-stage-curated", {
        body: { video_url: url, caption: caption.trim() || undefined },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(
        (data as any)?.summary_generated
          ? "Draft staged with AI summary — review and publish."
          : "Draft staged — review and add a summary before publishing.",
      );
      setVideoUrl("");
      setCaption("");
      onStaged((data as any).id);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !loading && onOpenChange(o)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Music2 className="h-5 w-5" /> Add from TikTok
          </DialogTitle>
          <DialogDescription>
            Paste a TikTok video URL. We'll pull the title, creator, and thumbnail via TikTok's
            oEmbed, generate an editorial summary, and stage a draft curated post for your review.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="tt-url">TikTok video URL *</Label>
            <Input
              id="tt-url"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://www.tiktok.com/@creator/video/7300000000000000000"
              disabled={loading}
            />
          </div>
          <div>
            <Label htmlFor="tt-caption">Caption or transcript (optional)</Label>
            <Textarea
              id="tt-caption"
              rows={4}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Paste the caption or a short transcript so the AI summary is grounded in what the video actually says."
              disabled={loading}
              maxLength={2000}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Without a caption, the summary stays generic. {caption.length}/2000
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Staging…
              </>
            ) : (
              "Stage draft"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddFromTikTokDialog;